import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import {
  BehaviorSubject,
  catchError,
  defer,
  filter,
  Observable,
  of,
  shareReplay,
  skip,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { environment } from '../../../../environments/environment';
import { avecRepli } from '../../rxjs/resultat-requete';

export const RolesApplicatifs = {
  admin: 'Admin',
  compta: 'Compta',
  tech: 'Tech',
  serviceClient: 'ServiceClient',
} as const;

// Quels rôles ouvrent quoi — source unique, consommée par les computed ci-dessous, par le
// menu latéral et par les guards de route. Toute évolution des droits se fait ici
export const RolesAcces = {
  // Menu Clients : Tech et Admin en complet, ServiceClient en restreint (options et fils de
  // l'eau modifiables, label et base de données en lecture seule)
  clients: [RolesApplicatifs.admin, RolesApplicatifs.tech, RolesApplicatifs.serviceClient],
  clientsComplet: [RolesApplicatifs.admin, RolesApplicatifs.tech],
  // Redditions, rapprochements et paramétrage
  comptabilite: [RolesApplicatifs.admin, RolesApplicatifs.compta],
} as const;

export function possedeUnRoleParmi(roles: readonly string[], rolesAutorises: readonly string[]): boolean {
  return roles.some(role => rolesAutorises.includes(role));
}

// Rôles applicatifs Azure (appRoles) de l'utilisateur connecté. Source unique : l'API, qui
// les lit dans le token d'accès. Ils sont déclarés sur l'app registration de l'API et
// n'apparaissent donc jamais dans le token d'identité du front, qui ne peut pas les lire
// lui-même — déclarer des appRoles côté front ne servirait à rien, le backend ne regardant
// que le token d'accès
@Injectable({
  providedIn: 'root',
})
export class RolesUtilisateurService {
  private msalService = inject(MsalService);
  private msalBroadcastService = inject(MsalBroadcastService);
  private httpClient = inject(HttpClient);

  // Faux tant que l'API des rôles n'a pas répondu : permet de distinguer « cet utilisateur
  // n'a aucun rôle » de « les rôles n'ont pas pu être chargés »
  rolesCharges = signal(false);

  // Un échec de chargement ne doit pas être mis en cache à vie : ce déclencheur permet de
  // relancer le fetch (rolesFrais$) tant que rolesCharges est faux, sans requête
  // supplémentaire une fois les rôles chargés
  private rechargement$ = new BehaviorSubject<void>(undefined);

  // Distingue « premier chargement encore en vol » (attendre sa réponse) d'« échec déjà
  // émis » (relancer et sauter la valeur périmée rejouée par shareReplay)
  private aDejaEmis = false;

  // Chargé après authentification, partagé entre le signal, le menu et les guards de route.
  // Le flux ne complète jamais : une relance réussie ré-émet, et les consommateurs
  // long-vécus (menu latéral, toSignal) se mettent à jour sans rechargement de page
  roles$ = this.msalBroadcastService.inProgress$.pipe(
    filter(status => status === InteractionStatus.None),
    filter(() => this.msalService.instance.getAllAccounts().length > 0),
    take(1),
    switchMap(() => this.rechargement$),
    // L'endpoint dédoublonne déjà les rôles renvoyés
    switchMap(() =>
      this.httpClient.get<string[]>(`${environment.backendUrl}utilisateurs/roles`).pipe(
        tap(() => this.rolesCharges.set(true)),
        catchError(() => of([] as string[])),
        // L'intercepteur global affiche l'erreur puis retourne une complétion vide : sans ce
        // repli, roles$ n'émettrait jamais — menu latéral vide et guards de route bloqués
        avecRepli<string[]>([]),
      ),
    ),
    tap(() => (this.aDejaEmis = true)),
    shareReplay({ bufferSize: 1, refCount: false }),
  );

  // Rôles chargés, ou premier chargement encore en vol : la prochaine valeur de roles$
  // suffit, aucune requête supplémentaire. Échec déjà émis : un nouveau fetch est déclenché
  // et c'est son résultat qui est émis — la valeur périmée rejouée par shareReplay est
  // sautée. Le next() part avant l'abonnement à prochaineEmission$, mais l'émission n'est
  // pas perdue : roles$ porte shareReplay({ refCount: false }), l'abonnement amont reste
  // donc vivant en permanence — passer refCount à true casserait cette méthode
  rolesFrais$(): Observable<string[]> {
    return defer(() => {
      if (this.rolesCharges() || !this.aDejaEmis) {
        return this.roles$.pipe(take(1));
      }

      const prochaineEmission$ = this.roles$.pipe(skip(1), take(1));
      this.rechargement$.next();
      return prochaineEmission$;
    });
  }

  roles = toSignal(this.roles$, { initialValue: [] as string[] });

  // Seul raccourci utilisé pour l'instant : les composants du module Clients distinguent
  // l'accès complet (Admin, Tech) de l'accès restreint (ServiceClient). Ailleurs, le menu et
  // les guards passent directement par RolesAcces et possedeUnRoleParmi
  accesClientsComplet = computed(() => possedeUnRoleParmi(this.roles(), RolesAcces.clientsComplet));
}
